import { FeeCollectionTiming, FeeType, FeeDetail } from "@/shared/api/model";
import { useCalculateLoanFees, useHasProductDisbursementFees } from "@/shared/api/client/loan-schedule";

/**
 * Interface for loan fee calculation response
 */
export interface LoanFeeCalculation {
  loanAmount: number;
  disbursementFees: FeeDetail[];
  repaymentFees: FeeDetail[];
  totalDisbursementFees: number;
  totalRepaymentFees: number;
  netDisbursementAmount: number;
  hasDisbursementFees: boolean;
  hasRepaymentFees: boolean;
}

/**
 * Hook for calculating loan fees during application process
 */
export const useLoanFeeCalculation = (
  glNo: string | undefined,
  loanAmount: number | undefined,
  loanPeriod?: number,
  loanBalanceData?: any
) => {
  const topUpSettlementAmount = loanBalanceData?.activeLoanBehavior === 'ALLOW_TOPUP' ||
    loanBalanceData?.activeLoanBehavior === 'ALLOW_REFINANCE'
    ? (Number(loanBalanceData.unpaidPrincipal) || 0) +
      (loanBalanceData.includeAccruedInterest ? (Number(loanBalanceData.unpaidAccruedInterest) || 0) : 0)
    : undefined;

  return useCalculateLoanFees(
    { glNo: glNo!, loanAmount: loanAmount!, loanPeriod, topUpSettlementAmount } as any,
    {
      query: {
        enabled: Boolean(glNo && loanAmount && loanAmount > 0),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
      }
    }
  );
};

/**
 * Hook for checking if a product has disbursement fees
 */
export const useProductDisbursementFees = (glNo: string | undefined) => {
  return useHasProductDisbursementFees(
    { glNo: glNo! },
    {
      query: {
        enabled: Boolean(glNo),
        staleTime: 10 * 60 * 1000, // 10 minutes - product config doesn't change often
        retry: 1,
      }
    }
  );
};

/**
 * Utility function to format fee amount for display
 */
export const formatFeeAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-TZ', {
    style: 'currency',
    currency: 'TZS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Utility function to format fee description for display
 */
export const formatFeeDescription = (fee: FeeDetail): string => {
  if (fee.feeType === 'PERCENTAGE') {
    return `${fee.feeValue || 0}% of loan amount`;
  } else {
    return `Fixed amount of ${formatFeeAmount(fee.feeValue || 0)}`;
  }
};

/**
 * Utility function to get fee timing display text
 */
export const getFeeTimingText = (timing: FeeDetail['collectionTiming']): string => {
  switch (timing) {
    case 'DISBURSEMENT':
      return 'Collected at disbursement';
    case 'REPAYMENT':
      return 'Collected during repayment';
    default:
      return timing || 'Unknown timing';
  }
};
